/**
 * Presentación de los vitales de rendimiento percibido.
 *
 * Módulo sin JSX para poder probarlo con el ejecutor de tests de Node, y
 * porque decidir cómo se lee un número no es cosa de la vista.
 */

export interface Referencia {
  bueno: string;
  que_es: string;
}

/**
 * Umbrales de referencia, para poder interpretar el valor.
 *
 * Los cinco primeros son los Core Web Vitals de Google. Los dos últimos vienen
 * del módulo nativo de Android y usan los umbrales de Play Vitals, por la misma
 * razón: si nuestra calificación no coincide con la que el cliente ve en su
 * consola de Play, no le va a creer a ninguna de las dos.
 */
export const REFERENCIA: Record<string, Referencia> = {
  LCP: { bueno: '≤ 2,5 s', que_es: 'cuánto tarda en verse el contenido principal' },
  INP: { bueno: '≤ 200 ms', que_es: 'cuánto tarda la página en responder a un clic' },
  CLS: { bueno: '≤ 0,1', que_es: 'cuánto se mueve el contenido mientras carga' },
  FCP: { bueno: '≤ 1,8 s', que_es: 'cuándo aparece el primer contenido' },
  TTFB: { bueno: '≤ 800 ms', que_es: 'cuánto tarda el servidor en responder' },
  cold_start: { bueno: '≤ 2 s', que_es: 'cuánto tarda la app en abrir desde cero' },
  jank: { bueno: '≤ 1%', que_es: 'qué proporción de cuadros se pierde al desplazar' },
};

/** Nombre legible: los de Android no son siglas conocidas como LCP o CLS. */
export const NOMBRE_VITAL: Record<string, string> = {
  cold_start: 'Arranque en frío',
  jank: 'Cuadros perdidos',
};

export function etiquetaDeVital(vital: string): string {
  return NOMBRE_VITAL[vital] ?? vital;
}

/**
 * CLS no tiene unidad y el jank es un porcentaje; el resto son milisegundos y
 * pasan a segundos al crecer.
 */
export function formatearVital(vital: string, valor: number): string {
  if (vital === 'CLS') return valor.toFixed(3);
  if (vital === 'jank') return `${valor.toFixed(2)}%`;
  if (valor >= 1000) return `${(valor / 1000).toFixed(2)} s`;
  return `${Math.round(valor)} ms`;
}

export type Calificacion = 'good' | 'needs-improvement' | 'poor';

/**
 * Califica según el criterio de Google: un sitio es bueno cuando al menos el
 * 75% de las visitas lo son. Por eso se mira el reparto y no el promedio, que
 * escondería a la cuarta parte que lo vive peor.
 */
export function calificarPorReparto(buenos: number, aMejorar: number): Calificacion {
  if (buenos >= 75) return 'good';
  if (buenos + aMejorar >= 75) return 'needs-improvement';
  return 'poor';
}
